import { WhatChanges2027 } from "@/components/what-changes-2027";
import { createFileRoute } from "@tanstack/react-router";
import { MarketingPage, Section } from "@/components/marketing-page";
import { marketingHead, type FAQ } from "@/lib/seo";
import { MEASURED, MEASURED_SOURCE } from "@/lib/measured-stats";

const PATH = "/ai-visibility-tracking";
const TITLE = "AI Visibility Tracking: ChatGPT, Gemini & Google AI | monroya.ai";
const DESCRIPTION =
  "Track AI visibility in ChatGPT, Gemini, Claude, Perplexity and Google AI Mode. See who AI recommends instead. Free plan, paid plans from $79/month.";
const UPDATED_ISO = "2026-10-26";

const TRACKING_FAQS: FAQ[] = [
  {
    q: "How does AI visibility tracking work?",
    a: "A tracking tool asks AI assistants the questions your buyers ask, on a fixed schedule, and saves every answer. It then records whether your company was named, recommended or cited as a source, and which competitors appeared instead. monroya.ai runs this across six AI surfaces and keeps the raw answers so every number can be checked.",
  },
  {
    q: "What should you track besides mentions?",
    a: "Track three things, not one. Named means the answer mentioned you. Recommended means the answer told the buyer to pick you. Cited means the assistant linked one of your pages as a source. Also track which competitors are named on each question and which outside pages get cited, because those pages are where you can get listed.",
  },
  {
    q: "Can you track Google AI Mode?",
    a: "Yes. Google AI Mode and AI Overviews can be tracked like chat assistants: the same buyer question is run in Google, the generated answer is saved, and the businesses and pages it names are recorded. monroya.ai includes both Google AI Overviews and Google AI Mode on paid plans, next to ChatGPT, Claude, Gemini and Perplexity.",
  },
  {
    q: "How much does AI visibility tracking cost?",
    a: "Prices range from about $29 a month for small prompt sets to several hundred dollars or more for enterprise platforms. monroya.ai has a free plan, and paid plans start at $79 a month for 50 tracked buyer questions across six AI surfaces. Higher plans add more questions, drafts and daily checks for top questions.",
  },
];
const TERM_DESC =
  "The practice of measuring how a brand or product appears in generative AI answers, mentions in answer text, citations in source lists, and ranking versus competitors across major chat assistants.";

const GEO_FAQS: FAQ[] = [
  {
    q: "What is GEO?",
    a: "GEO (Generative Engine Optimization) is the practice of getting your brand named and cited inside AI-generated answers on retrieval-augmented surfaces, ChatGPT Search, Perplexity, Google AI Overviews, Copilot, and Gemini with browsing, where the model pulls live sources at query time.",
  },
  {
    q: "What is AI visibility?",
    a: "AI visibility is how often and how prominently a brand appears inside AI-generated answers: whether it is mentioned in the answer text, whether its domain is cited as a source, and how it ranks against the competitors named alongside it. GEO is the work; AI visibility is the measurement layer that tells you whether the work landed.",
  },
  {
    q: "How is GEO different from traditional SEO?",
    a: "SEO optimizes for a ranked list of ten blue links. GEO optimizes for the single generated answer that names two to five vendors. The levers are different: GEO prizes citable statistics, comparison pages, structured data, review-site presence, and community threads over traditional ranking signals alone.",
  },
  {
    q: "Why does GEO matter for B2B brands?",
    a: "A meaningful share of B2B product discovery now happens inside AI chats. Buyers ask 'what's the best X for Y' and read a short answer naming a few vendors. If your brand isn't cited, you don't make the shortlist, regardless of your organic search position.",
  },
  {
    q: "What moves the needle in GEO?",
    a: "Six things: citable comparison content that models can quote, presence on review sites and directories, active Reddit and community threads, and structured data that helps retrieval systems understand your entity. Each gives the model a source it can pull at query time.",
  },
  {
    q: "How do you measure GEO success?",
    a: "Track mention rate, citation rate, and share of voice across the prompts your buyers actually ask, split by provider and buyer stage. A GEO program is working when your brand appears in more answers and is cited as a source more often over time.",
  },
  {
    q: "Which tools track GEO and AI visibility?",
    a: "Tools in this category include Monroya, Profound, Otterly, AthenaHQ, Peec AI, and Scrunch AI. Monroya runs each buyer prompt multiple times per model across six AI surfaces, separates mentions from citations, splits results by buyer stage (discovery, evaluation, decision, retention), and traces every score back to the raw model response.",
  },
  {
    q: "How long does GEO take to show results?",
    a: "On retrieval-augmented surfaces, new comparison pages, review-site listings, and community threads can change answers in days to weeks, because the model fetches sources at query time. Changes that depend on the base model's own knowledge take months, in step with model refresh cycles.",
  },
];


const FAQS: FAQ[] = [
  ...TRACKING_FAQS,
  {
    q: "Is AI visibility tracking the same as SEO?",
    a: "No. SEO measures position on a search engine results page; AI visibility measures presence inside a generated answer. The mechanics differ: AI answers cite from a narrower set of sources, weight authority differently, and rarely show ten options.",
  },
  {
    q: "Is this the same as Answer Engine Optimization (AEO) or Generative Engine Optimization (GEO)?",
    a: "AEO and GEO are the practitioner names for the work; AI visibility tracking is the measurement layer underneath. You need the tracking to know whether your AEO/GEO work is doing anything.",
  },
  {
    q: "Which AI assistants should I track?",
    a: "At minimum, the six with measurable buyer share: ChatGPT, Claude, Gemini, Perplexity, Google AI Overviews, and Google AI Mode. Adding Copilot or Grok is reasonable for some categories, but starting with all six covers the majority of B2B buyer behavior in 2026.",
  },
  {
    q: "How often does AI visibility actually change?",
    a: "Daily for the noise, weekly for the signal. A single prompt can return different answers minute to minute, so a credible tracking tool aggregates many runs per day before reporting movement.",
  },
  {
    q: "Why now?",
    a: "More than a third of B2B buyers now start product research in an AI chat instead of Google. If the answer doesn't include you, you've lost the deal before your site ever loads.",
  },
  {
    q: "How do I evaluate vendors in this category?",
    a: "Ask three things: how many runs per prompt per day, do they separate mentions from citations, and can you audit any score back to the raw model response. If the answer to any of those is unclear, the score is probably noise.",
  },
  ...GEO_FAQS,
  {
    q: "What is the difference between GEO and LLMO in 2026?",
    a: "GEO (Generative Engine Optimization) is the practice of getting your brand named and cited inside AI-generated answers on retrieval-augmented surfaces, ChatGPT Search, Perplexity, Google AI Overviews, Copilot, where the model pulls live sources at query time. LLMO (Large Language Model Optimization) is the practice of getting your brand represented in the model's underlying weights and default behavior, so it is named even without live retrieval. GEO is a content, citation, and source-selection problem; LLMO is a training-data, entity, and reputation problem. Both roll up into AI visibility, which is the measurement layer that tracks whether either kind of work is landing.",
  },
  {
    q: "Do I need to do both GEO and LLMO?",
    a: "In 2026 most B2B answers are retrieval-augmented, so GEO moves the score first, new comparison pages, review-site presence, and Reddit threads show up in days to weeks. LLMO is slower and mostly compounding: consistent entity descriptions, Wikipedia and Wikidata coverage, and third-party mentions harden the way base models describe you in refresh cycles that take months. Track both, but prioritize GEO for near-term lift.",
  },
];




export const Route = createFileRoute("/ai-visibility-tracking")({
  head: () =>
    marketingHead({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "AI visibility tracking", path: PATH },
      ],
      faqs: FAQS,
      dateModified: UPDATED_ISO,
      definedTerm: {
        name: "AI visibility tracking",
        description: TERM_DESC,
      },
    }),
  component: AIVisibilityPage,
});

function AIVisibilityPage() {
  return (
    <MarketingPage
      stamp="Definition"
      title="AI visibility tracking."
      dek="A practitioner's definition. What it measures, why it matters now, and how to tell good tools from noisy ones."
      updated="October 2026"
      faqs={FAQS}
      related={[
        {
          label: "Run the free AI visibility tool",
          to: "/check",
          description: "Instant score across ChatGPT, Claude, Gemini, Perplexity, Google AI Overviews and Google AI Mode",
        },
        {
          label: "Answer Engine Optimization guide",
          to: "/answer-engine-optimization",
          description: "How AEO differs from GEO and SEO",
        },
        {
          label: "LLM optimization (LLMO) guide",
          to: "/large-language-model-optimization",
          description: "Shaping what models say about you without retrieval",
        },
        {
          label: "Read the full Monroya methodology",
          to: "/how-it-works",
          description: "Multi-run scanning, scoring, draft generation",
        },

        {
          label: "Buyer journey intelligence",
          to: "/buyer-journey-intelligence",
          description: "How visibility shifts by funnel stage",
        },
        {
          label: "Looking at alternatives in this category",
          to: "/vs",
          description: "Profound, Otterly, AthenaHQ compared",
        },
      ]}
    >
      <p className="text-lg leading-relaxed text-ink">
        <strong className="text-ink">AI visibility tracking</strong> means
        asking ChatGPT, Gemini, Claude, Perplexity and Google AI the questions
        your buyers ask, every week, and recording whether your company is
        named, recommended or cited, and who gets picked instead. monroya.ai
        tracks all six AI surfaces, saves every answer, and turns each gap into
        a job. Start with a free check; paid plans start at $79/month.
      </p>

      <Section heading="Google AI Mode and AI Overviews tracking">
        <p>
          Google now answers many searches itself. AI Overviews sit above the
          normal results, and AI Mode is a separate chat-style answer. AI mode
          tracking works the same way as tracking a chat assistant: the same
          buyer question is run in Google on a schedule, the generated answer
          is saved, and the businesses and pages it names are recorded.
        </p>
        <p>
          monroya.ai reports Google AI Overviews and Google AI Mode next to
          ChatGPT, Claude, Gemini and Perplexity, so you can see when Google
          names a competitor that the chat assistants do not. When Google does
          not return an AI answer for a question, the result shows as "not
          checked" rather than as a miss.
        </p>
      </Section>

      <Section heading="What our scan data shows">

        <p>
          Four numbers counted from our own scans across ChatGPT, Claude,
          Gemini, and Perplexity frame why AI visibility tracking matters right
          now:
        </p>
        <ul className="mt-2 space-y-2 pl-5 [&amp;>li]:list-disc">
          <li>
            <strong>
              More than four in ten answers name no brand at all.
            </strong>{" "}
            Of {MEASURED.answers.toLocaleString()} assistant answers recorded
            for {MEASURED.questions.toLocaleString()} tracked buyer questions,
            {" "}{MEASURED.anyBrandPct}% named at least one company.
          </li>
          <li>
            <strong>
              Discovery-stage answers name a brand{" "}
              {MEASURED.stageAnyBrandPct.discovery}% of the time, against{" "}
              {MEASURED.stageAnyBrandPct.decision}% at decision
            </strong>{" "}
            - brands are far less visible while buyers are still framing the
            problem than when they are comparing named vendors.
          </li>
          <li>
            <strong>
              Evaluation-stage answers name a brand{" "}
              {MEASURED.stageAnyBrandPct.evaluation}% of the time
            </strong>
            , so the middle of the funnel is crowded: the question is which
            company gets named, not whether one does.
          </li>
          <li>
            <strong>
              {MEASURED.citations.toLocaleString()} cited sources came from{" "}
              {MEASURED.domains.toLocaleString()} different domains
            </strong>{" "}
            - no single publication decides who gets cited.
          </li>
        </ul>
        <p className="mt-3 text-sm text-muted-foreground">
          {MEASURED_SOURCE}. Recounted each time these figures change.
        </p>
      </Section>

      <Section heading="What it is">
        <p>
          AI visibility tracking is the practice of measuring how a brand or
          product appears in answers generated by large language model
          assistants, ChatGPT, Claude, Gemini, Perplexity, Google AI Overviews and Google AI Mode, and others. It
          captures three things: whether the brand is mentioned in the answer
          text, whether the brand's domain is cited in the source list, and how
          the brand ranks against competitors that get named alongside it.
        </p>

        <p>
          It is distinct from web analytics (which measures who reaches your
          site) and from traditional SEO (which measures position on a search
          engine results page). It sits one layer earlier in the funnel: the
          answer the buyer reads before they ever consider clicking through. In
          our own scans, {MEASURED.anyBrandPct}% of answers named at least one
          company, and the assistant decided which one without the buyer
          visiting a single site.
        </p>
      </Section>

      <Section heading="Why it matters now">
        <p>
          As of 2026, a meaningful share of B2B product discovery happens
          inside an AI chat rather than a Google query. Buyers ask "what's
          the best X for Y" and read a six-paragraph answer naming three
          vendors. The shortlist is set before any site is visited. If your
          brand isn't in that answer, you don't get evaluated.
        </p>
        <p>
          The shift matters more than the SEO transition of the early 2000s
          because there's no second page. An AI answer typically names two to
          five options. Position eleven on Google was disappointing but
          recoverable. Absent from the answer is invisible.
        </p>
      </Section>

      <Section heading="Why brand monitoring and Reddit monitoring matter">
        <p>
          Tracking presence is not enough. You also need to watch how your brand
          is being characterized, where the underlying claims come from, and
          whether the sources assistants pull from still match reality, because
          an out-of-date page about you can be quoted as current fact.
        </p>
        <p>
          Community and social sources are part of that picture rather than the
          whole of it. Of {MEASURED.citations.toLocaleString()} cited sources we
          recorded, editorial pages accounted for 21.9%, analyst pages 11.8%,
          review sites 11.6%, social 7.2%, and community forums such as Reddit
          1.6%. Watching one of those in isolation gives a misleading read.
        </p>
        <p className="text-sm text-muted-foreground">{MEASURED_SOURCE}.</p>
      </Section>

      <Section heading="Economic impact of AI search optimization">
        <p>
          We do not publish a market-size forecast, because we cannot measure
          one. What we can measure is who gets named: across{" "}
          {MEASURED.answers.toLocaleString()} recorded answers, the brands that
          appear are the ones with retrievable, sourced pages behind them. Every
          answer that names a competitor instead of you is a buying conversation
          you were not part of, and it leaves no trace in your analytics.
        </p>
        <p className="text-sm text-muted-foreground">{MEASURED_SOURCE}.</p>
      </Section>

      <Section heading="AI visibility ROI for B2B SaaS">
        <p>
          The ROI case is a comparison between two states of the same
          pipeline: the demand that happens where you cannot see it, and the
          demand that lands on you because an assistant named you first.
        </p>
        <p className="mt-4 font-medium text-ink">
          The cost of dark social invisibility
        </p>
        <ul className="mt-2 space-y-2 list-disc pl-5">
          <li>
            <strong>The evaluation happens off your property.</strong> A
            buyer asks an assistant to shortlist vendors, reads a synthesized
            answer, and never visits a site you can instrument. No session,
            no form fill, no UTM.
          </li>
          <li>
            <strong>Attribution reports it as direct or branded.</strong> The
            deal that started in an AI answer shows up in your CRM as
            self-sourced, so the channel that actually created it gets no
            budget next quarter.
          </li>
          <li>
            <strong>You lose without a loss record.</strong> Being absent
            from a shortlist produces no lost-deal entry, because you were
            never in the deal. In Monroya's own scan data, the average
            discovery-stage inclusion rate is 7.6%, most brands are missing
            from the conversation entirely at the stage that sets the
            shortlist.
          </li>
          <li>
            <strong>Mentions without citations compound the problem.</strong>{" "}
            78.9% of the brands we track have never been cited in a tracked
            answer: they get named, but no link points back, so the assistant
            sends the click to whoever documented the answer instead.
          </li>
          <li>
            <strong>The gap widens quietly.</strong> A competitor who fixes
            this earns compounding source coverage while your baseline stays
            flat, and nothing in your analytics stack raises a flag.
          </li>
        </ul>
        <p className="mt-6 font-medium text-ink">
          The pipeline value of being the #1 recommended tool in Perplexity
        </p>
        <ul className="mt-2 space-y-2 list-disc pl-5">
          <li>
            <strong>You are the default, not an option.</strong> The
            first-named vendor in a synthesized answer functions as the
            recommendation. The buyer arrives already anchored rather than
            comparing you cold against four alternatives.
          </li>
          <li>
            <strong>Traffic arrives late in the journey.</strong> Perplexity
            cites its sources inline, so the click that follows a
            vendor-selection answer is a buyer who has already read the
            comparison, closer to a demo request than to a blog reader.
          </li>
          <li>
            <strong>Sales cycles shorten.</strong> Third-party synthesis does
            the differentiation work your team would otherwise repeat on
            every first call.
          </li>
          <li>
            <strong>The position is durable.</strong> Being named rests on
            source coverage across documentation, comparison pages, and
            reviewer evidence, which does not reset on a bid or a campaign
            end date the way paid placement does.
          </li>
          <li>
            <strong>It becomes measurable.</strong> Once you track mention
            rate, citation rate, and position by prompt, the channel produces
            a number you can defend in a budget review instead of an
            anecdote.
          </li>
        </ul>
        <p className="mt-4">
          The practical ROI question is not "what is AI visibility worth in
          the abstract" but "what share of our decision-stage prompts name a
          competitor first, and what is one of those deals worth." Multiply
          your average contract value by the share of vendor-selection
          prompts you are absent from, and the tracking cost stops being the
          interesting number.
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          Inclusion and citation figures are from Monroya's aggregated
          customer scans across six AI surfaces,
          90-day rolling window.
        </p>
      </Section>

      <Section heading="How it works">
        <p>
          A tracking tool maintains a set of prompts that represent the
          questions your buyers actually ask. It runs those prompts on a
          schedule against multiple AI providers, parses each response, and
          records who got mentioned, who got cited, and in what order. Done
          credibly, this means many runs per prompt per day, single-shot
          sampling is too noisy to be useful.
        </p>
        <p>
          The output is a time series: your share of voice in this prompt
          set, broken down by provider, by persona, and by competitor. Good
          tools layer opportunity detection on top, surfacing the specific
          gaps where closing them would move the score the most.
        </p>
      </Section>

      <Section id="what-is-geo" heading="What is GEO (Generative Engine Optimization)?">
        <p>
          <strong className="text-ink">
            Generative Engine Optimization (GEO)
          </strong>{" "}
          is the practice of getting your brand named and cited inside
          AI-generated answers on retrieval-augmented surfaces, ChatGPT
          Search, Perplexity, Google AI Overviews, Copilot, and Gemini with
          browsing. Unlike SEO, which targets a ranked list of links, GEO
          targets the single answer that names two to five vendors and shapes
          the buyer's shortlist.
        </p>
        <p>
          The questions below are the definitional ones buyers and teams ask
          when they first meet the category. Each answer is marked up as
          FAQPage structured data so assistants can extract it directly.
        </p>
        <dl className="mt-6 space-y-5">
          {GEO_FAQS.map(({ q, a }) => (
            <div key={q}>
              <dt className="font-medium text-ink">{q}</dt>
              <dd className="mt-1 text-ink/80">{a}</dd>
            </div>
          ))}
        </dl>
      </Section>


      <Section heading="The difference between GEO and LLMO in 2026">
        <p>
          The two acronyms get used interchangeably and shouldn't be.{" "}
          <strong className="text-ink">
            Generative Engine Optimization (GEO)
          </strong>{" "}
          is the practice of getting your brand named and cited inside AI
          answers on <em>retrieval-augmented</em> surfaces, ChatGPT
          Search, Perplexity, Google AI Overviews, Copilot, Gemini with
          browsing, where the model pulls live sources at query time.{" "}
          <strong className="text-ink">
            Large Language Model Optimization (LLMO)
          </strong>{" "}
          is the practice of getting your brand represented in the
          model's underlying weights and default behavior, so it is named
          even when no live retrieval happens.
        </p>
        <p>
          The distinction matters because the two disciplines use
          different levers, on different timelines, measured against
          different surfaces:
        </p>
        <div className="mt-4 overflow-hidden rounded-lg border border-border">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-panel">
              <tr className="text-left">
                <th className="border-b border-border p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                  Dimension
                </th>
                <th className="border-b border-border p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                  GEO
                </th>
                <th className="border-b border-border p-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                  LLMO
                </th>
              </tr>
            </thead>
            <tbody className="text-ink/85">
              <tr>
                <td className="border-b border-border p-3 font-medium text-ink">
                  Surface
                </td>
                <td className="border-b border-border p-3">
                  Retrieval-augmented answers (ChatGPT Search, Perplexity,
                  AI Overviews, Copilot)
                </td>
                <td className="border-b border-border p-3">
                  Base-model answers with no browsing (raw ChatGPT,
                  Claude, Gemini defaults)
                </td>
              </tr>
              <tr>
                <td className="border-b border-border p-3 font-medium text-ink">
                  What moves the needle
                </td>
                <td className="border-b border-border p-3">
                  Comparison pages, review-site listings, Reddit and
                  community threads, structured data, citable statistics
                </td>
                <td className="border-b border-border p-3">
                  Consistent entity descriptions, Wikipedia and Wikidata
                  coverage, high-authority third-party mentions, category
                  co-occurrence at scale
                </td>
              </tr>
              <tr>
                <td className="border-b border-border p-3 font-medium text-ink">
                  Time to first lift
                </td>
                <td className="border-b border-border p-3">
                  Days to weeks
                </td>
                <td className="border-b border-border p-3">
                  Months, in step with model refresh cycles
                </td>
              </tr>
              <tr>
                <td className="border-b border-border p-3 font-medium text-ink">
                  Owner
                </td>
                <td className="border-b border-border p-3">
                  Marketing / content / SEO
                </td>
                <td className="border-b border-border p-3">
                  Brand / PR / comms, with product-marketing input
                </td>
              </tr>
              <tr>
                <td className="p-3 font-medium text-ink">
                  How it's measured
                </td>
                <td className="p-3">
                  Presence and citation rate on retrieval-augmented
                  answers, source list share
                </td>
                <td className="p-3">
                  Presence and share of voice on browsing-off answers,
                  entity-description accuracy
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-4">
          In practice, GEO is where near-term score movement lives, a
          well-placed comparison page or a well-answered Reddit thread can
          land in a retrieval-augmented answer within a scan cycle. LLMO
          is the compounding layer underneath: entity consistency and
          third-party authority that decides how the base model describes
          you the next time it is refreshed.
        </p>
        <p>
          <strong className="text-ink">AI visibility tracking</strong>{" "}
          sits above both. It measures whether either kind of work is
          landing, on retrieval-augmented surfaces for GEO, and on
          browsing-off surfaces for LLMO, and attributes lift to the
          specific asset or change that caused it. Treating GEO and LLMO
          as one bucket ("AI SEO") makes it impossible to tell which lever
          is working, which is why the measurement layer keeps them
          separate.
        </p>
      </Section>


      <Section heading="How to evaluate tools">
        <p>
          Three questions separate signal from noise. First, how many runs
          per prompt per day, anything below five is likely to misreport
          movement as a trend. Second, do they distinguish mentions from
          citations, collapsing them hides which kind of work will fix the
          gap. Third, can you audit any number back to the raw model
          response, if not, the score is taken on faith.
        </p>
        <p>
          Secondary questions: how many providers, do they support persona
          framing, do they generate the asset that closes the gap, and is the
          pricing usage-based or seat-based for the team size you actually
          need.
        </p>
      </Section>

      <Section heading="What good looks like">
        <p>
          A credible AI visibility report tells you, for a given prompt set
          and persona, your share of voice today, what changed this week,
          which competitor moved against you, and the top three actions that
          would close the biggest gaps. Anything less is a dashboard, not a
          tool.
        </p>
        <p>
          The job of AI visibility tracking is to make sure you are one of the
          vendors the assistant summarizes. In our scans, the companies that get
          named repeatedly are the ones whose facts are easy to retrieve and
          easy to check, not the ones with the most pages.
        </p>
      </Section>

      <WhatChanges2027 />
    </MarketingPage>
  );
}
